import { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

export default function Login() {
  const { user, checking, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("admin@nextprovider.com.au");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [org, setOrg] = useState(null);

  useEffect(() => { api.get("/org/public").then((r) => setOrg(r.data)).catch(() => {}); }, []);

  if (checking) return null;
  if (user) return <Navigate to="/" replace />;

  async function onSubmit(e) {
    e.preventDefault();
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success("Welcome back");
      nav("/");
    } else {
      toast.error(res.error || "Login failed");
    }
  }

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[#F8FAFC]">
      {/* left panel */}
      <div className="hidden md:flex relative bg-[#0D1B2A] text-white overflow-hidden">
        <div
          className="absolute inset-0 opacity-40 bg-cover bg-center"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1669295384050-a1d4357bd1d7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwxfHxkYXJrJTIwYmx1ZSUyMHRlYWwlMjBhYnN0cmFjdCUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzgyOTAzODAwfDA&ixlib=rb-4.1.0&q=85)",
          }}
        />
        <div className="relative z-10 flex flex-col justify-between p-12">
          <div className="wordmark text-2xl flex items-center">
            {org?.has_logo ? (
              <img
                src={`${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api/org/logo?v=${org?.logo_updated_at ? new Date(org.logo_updated_at).getTime() : ""}`}
                alt={org?.name || "Logo"}
                className="h-12 max-w-[240px] object-contain bg-white rounded p-2"
                data-testid="login-org-logo"
              />
            ) : (
              <><span className="np-mark">NP</span>{org?.name || "Next Provider"}</>
            )}
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#1A7F6E] mb-4">Consultant workspace</p>
            <h1 className="text-4xl font-semibold tracking-tight leading-tight max-w-md">
              {org?.tagline || "NDIS registration discovery, mapped in one session."}
            </h1>
            <p className="mt-4 text-slate-300 max-w-md text-sm leading-relaxed">
              Capture intake, resolve audit pathway, and produce a client-ready report during the discovery call.
            </p>
          </div>
          <p className="text-xs text-slate-400">nextprovider.com.au</p>
        </div>
      </div>

      {/* right panel */}
      <div className="flex items-center justify-center p-6 md:p-12">
        <Card className="w-full max-w-md border-slate-200 shadow-sm">
          <CardContent className="p-8">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">Sign in</p>
            <h2 className="text-2xl font-semibold tracking-tight text-[#0D1B2A] mb-6">Consultant login</h2>
            <form onSubmit={onSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="login-email-input"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password-input"
                  required
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#1A7F6E] hover:bg-[#136053] text-white"
                data-testid="login-submit-button"
              >
                {loading ? "Signing in…" : "Sign in"}
              </Button>
            </form>
            <p className="mt-6 text-xs text-slate-500">
              Internal tool. Contact your administrator to request access.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
